import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLogoutMutation } from '@/features/auth/hooks/use-auth'
import { useAuthStore } from '@/features/auth/stores/auth-store'
import { ThemeToggle } from '@/features/theme/components/theme-toggle'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  FALLBACK_PREMIUM_PRICING,
  PREMIUM_PRICING_QUERY_KEY,
  fetchPremiumPricing,
  type PremiumPricingResponse,
} from '@/features/account/api/account-api'
import { PremiumPaymentModal, type PremiumOption } from '@/features/account/components/premium-payment-modal'
import { useQuery } from '@tanstack/react-query'
import { GleamWordmark } from '@/shared/ui/gleam-branding'
import { usePageSeo } from '@/shared/seo/page-seo'

const navItems: { to: string; labelKey: string }[] = [
  { to: '/app/chat', labelKey: 'nav.chat' },
  { to: '/app/models', labelKey: 'nav.models' },
  { to: '/app/settings', labelKey: 'nav.settings' },
]

export function AppLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const logoutMutation = useLogoutMutation()
  const user = useAuthStore((state) => state.user)
  const isGuest = Boolean(user?.isGuest)
  const [headerHidden, setHeaderHidden] = useState(false)
  const lastScrollRef = useRef(0)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const isChatRoute = location.pathname.startsWith('/app/chat')

  const { data: premiumPricing, isLoading: isPricingLoading } = useQuery<PremiumPricingResponse>({
    queryKey: PREMIUM_PRICING_QUERY_KEY,
    queryFn: fetchPremiumPricing,
    staleTime: 10 * 60_000,
    enabled: !isGuest,
  })
  const visibleNavItems = isGuest ? navItems.filter((item) => item.to !== '/app/settings') : navItems

  usePageSeo({
    title: 'GLEAM app',
    description: 'Private GLEAM app workspace.',
    canonicalPath: location.pathname,
    robots: 'noindex, nofollow',
  })

  const premiumOptions: PremiumOption[] = (() => {
    const pricing = premiumPricing ?? FALLBACK_PREMIUM_PRICING
    const mapProvider = (source?: Record<string, number>) =>
      Object.entries(source ?? {})
        .map(([month, amount]) => ({ months: Number(month), amount }))
        .filter((item) => item.months > 0 && Number.isFinite(item.amount))
        .sort((a, b) => a.months - b.months)

    return [
      {
        purpose: 'Premium1',
        title: 'Gleam Premium',
        description: t('profile.premium1_description'),
        providers: {
          CryptoPay: mapProvider(pricing.premium1?.cryptoPay),
        },
      },
      {
        purpose: 'Premium2',
        title: 'Gleam X',
        description: t('profile.premium2_description'),
        providers: {
          CryptoPay: mapProvider(pricing.premium2?.cryptoPay),
        },
      },
    ]
  })()

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync()
    } finally {
      void navigate('/login', { replace: true })
    }
  }

  useEffect(() => {
    if (isChatRoute) {
      setHeaderHidden(false)
      return
    }

    const handleScroll = () => {
      const current = window.scrollY
      const last = lastScrollRef.current
      if (current > last + 20 && current > 60) {
        setHeaderHidden(true)
      } else if (current < last - 20 || current <= 60) {
        setHeaderHidden(false)
      }
      lastScrollRef.current = current
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isChatRoute])

  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobileNavOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileNavOpen])

  return (
    <div className={cn('bg-[#0b0a0d] text-slate-100', isChatRoute ? 'h-dvh overflow-hidden' : 'min-h-screen')}>
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(255,46,166,0.16),transparent_35%),linear-gradient(180deg,#0b0a0d_0%,#0b0a0d_100%)]"
        aria-hidden="true"
      />
      <div className={cn('flex min-h-screen flex-col', isChatRoute && 'h-dvh min-h-0 overflow-hidden')}>
        <header
          className={cn(
            'bg-[#0b0a0d]/92 sticky top-0 z-40 shrink-0 border-b border-white/[0.06] backdrop-blur-xl transition-transform duration-300',
            headerHidden && !isChatRoute ? '-translate-y-full' : 'translate-y-0',
          )}
        >
          <div className="mx-auto grid h-16 w-full max-w-screen-2xl grid-cols-[auto_1fr_auto] items-center gap-3 px-4 sm:px-6 md:h-20 lg:px-10">
            <Link
              to="/app/chat"
              className="inline-flex items-center transition-opacity hover:opacity-85"
              aria-label="GLEAM"
            >
              <GleamWordmark className="text-[30px] sm:text-[38px] md:text-[42px]" />
            </Link>
            <nav className="hidden items-center justify-center gap-8 md:flex">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'border-b border-transparent p-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/50 transition-colors duration-300',
                      'hover:text-white',
                      isActive && 'border-primary text-white',
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center justify-end gap-2">
              {!isGuest ? (
                <Button
                  variant="primary"
                  size="sm"
                  className="hidden h-10 rounded-none border-none bg-primary px-5 text-[10px] font-black uppercase tracking-[0.24em] text-white shadow-none transition hover:bg-primary-soft sm:inline-flex"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  {t('nav.buy_tokens')}
                </Button>
              ) : null}
              <button
                type="button"
                className="text-white/72 inline-flex size-11 items-center justify-center border border-white/[0.12] bg-black/40 transition hover:border-primary hover:text-white md:size-10"
                onClick={() => {
                  void handleLogout()
                }}
                aria-label={t('nav.logout')}
                disabled={logoutMutation.isPending}
              >
                <LogoutIcon />
              </button>
              <ThemeToggle className="hidden size-10 rounded-none border border-white/[0.08] bg-black/30 text-primary-soft hover:border-white/[0.16] hover:bg-black/50 lg:inline-flex" />
              <Button
                variant="ghost"
                size="icon"
                className="text-white/72 size-11 rounded-none border border-white/[0.12] bg-black/40 hover:border-primary hover:bg-black/50 hover:text-white md:hidden"
                aria-label={t('nav.menu_aria')}
                aria-expanded={isMobileNavOpen}
                onClick={() => setIsMobileNavOpen(true)}
              >
                <MenuIcon />
              </Button>
            </div>
          </div>
        </header>

        <main className={cn('bg-[#0b0a0d]/96 flex-1', isChatRoute && 'min-h-0 overflow-hidden')}>
          <div
            className={cn(
              'mx-auto w-full max-w-screen-2xl',
              isChatRoute
                ? 'h-full min-h-0 overflow-hidden lg:px-8 lg:py-6 xl:px-12'
                : 'flex flex-col gap-10 px-4 py-12 sm:px-6 lg:px-12',
            )}
          >
            <Outlet />
          </div>
        </main>

        {isMobileNavOpen ? (
          <MobileNavigationDrawer
            items={visibleNavItems}
            isGuest={isGuest}
            onClose={() => setIsMobileNavOpen(false)}
            onOpenPremium={() => {
              setIsMobileNavOpen(false)
              setIsPaymentModalOpen(true)
            }}
          />
        ) : null}

        {isPaymentModalOpen && !isGuest ? (
          <PremiumPaymentModal
            options={premiumOptions}
            isLoading={isPricingLoading}
            onClose={() => setIsPaymentModalOpen(false)}
          />
        ) : null}
      </div>
    </div>
  )
}

function MobileNavigationDrawer({
  items,
  isGuest,
  onClose,
  onOpenPremium,
}: {
  items: typeof navItems
  isGuest: boolean
  onClose: () => void
  onOpenPremium: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label={t('nav.menu_aria')}>
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label={t('nav.close_aria')}
      />
      <aside className="absolute inset-y-0 right-0 flex w-[min(86vw,340px)] flex-col border-l border-white/10 bg-[#100e14] p-5 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/[0.08] pb-5">
          <GleamWordmark className="text-[30px]" />
          <button
            type="button"
            className="inline-flex size-11 items-center justify-center border border-white/[0.12] bg-black/30 text-white/70 transition hover:border-primary hover:text-white"
            onClick={onClose}
            aria-label={t('nav.close_aria')}
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="mt-6 grid gap-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex min-h-12 items-center border border-transparent px-4 text-sm font-semibold uppercase tracking-[0.22em] text-white/55 transition',
                  'hover:border-white/10 hover:bg-white/[0.05] hover:text-white',
                  isActive && 'border-primary/35 bg-primary/10 text-white',
                )
              }
            >
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
        {!isGuest ? (
          <Button
            type="button"
            className="mt-6 h-12 rounded-none border-none bg-primary text-xs font-black uppercase tracking-[0.22em] text-white"
            onClick={onOpenPremium}
          >
            {t('nav.buy_tokens')}
          </Button>
        ) : null}
        <div className="mt-auto border-t border-white/[0.08] pt-5">
          <ThemeToggle className="size-11 rounded-none border border-white/[0.12] bg-black/30 text-primary-soft" />
        </div>
      </aside>
    </div>
  )
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4 7a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Z"
      />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden="true">
      <path
        fill="currentColor"
        d="M15.29 7.29a1 1 0 0 1 1.41 0l3.99 4a1 1 0 0 1 0 1.42l-3.99 4a1 1 0 0 1-1.41-1.42L17.58 13H9a1 1 0 1 1 0-2h8.58l-2.29-2.29a1 1 0 0 1 0-1.42ZM4 5a2 2 0 0 1 2-2h5a1 1 0 1 1 0 2H6v14h5a1 1 0 1 1 0 2H6a2 2 0 0 1-2-2V5Z"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.29 6.29a1 1 0 0 1 1.42 0L12 10.59l4.29-4.3a1 1 0 1 1 1.42 1.42L13.41 12l4.3 4.29a1 1 0 0 1-1.42 1.42L12 13.41l-4.29 4.3a1 1 0 0 1-1.42-1.42l4.3-4.29-4.3-4.29a1 1 0 0 1 0-1.42Z"
      />
    </svg>
  )
}
